import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  ClearedExternalLink,
  ClearedLink,
} from '../../../../common/controls/Controls';
import Ripples from 'react-ripples';
import { debounce } from 'lodash';
import { getMenuItemUrl, getTitleOfItem } from '../MainNavigation';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { useLocation } from 'react-router';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import firstMenuItemRef from "react-big-calendar/dist/react-big-calendar.js";

const MobileMenu = ({ menu, withHideClass, closeMenu }) => {
  const [openedItemId, openItem] = useState(undefined);
  const firstMenuItemRef = useRef(null);
  const location = useLocation();
  const [currentLocation, saveLocation] = useState(
    location && location.pathname,
  );

  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    if (currentLocation !== (location.pathname || '')) {
      // For case if user goes to another page -> f location.pathname change let's close menu for him to see new page
      openItem(undefined);
      closeMenu();
    }
  }, [location, currentLocation, closeMenu]);

  useEffect(() => {
    if (firstMenuItemRef.current) {
      firstMenuItemRef.current.focus();
    }
  }, []);

  if (!menu) {
    return null;
  }

  const openNewLevelMenu = (itemIdForOpen) => {
    // debounce tam je preto aby sa prechod na inu podstranku zavolal az za 300 mili sekund
    // aby bolo vidiet ten cierny prechod
    debounce(() => openItem(itemIdForOpen), 300)();
  };

  const mobileMenuActionButtonStyle = { margin: '10px', width: '100%' };

  const firstLevelMenuItem = (item) => (
    <li className={`parent-level ${!openedItemId && 'show'}`}>
      <Ripples onClick={() => openNewLevelMenu(item && item.id)}>
        <button
          type="button"
          className="mobile-menu-action-button"
          ref={menu && menu[0] && menu[0].id === item.id ? firstMenuItemRef : null}
          style={mobileMenuActionButtonStyle}
          onClick={() => openNewLevelMenu(item && item.id)}
        >
          {getTitleOfItem(item, isEnglish)}
          <div className="icon-right">
            <RightOutlined />
          </div>
        </button>
      </Ripples>
    </li>
  );

  const childLevelMenuItem = (item) => {
    const hasChildren = !!(item.children && item.children.length > 0);

    const getItemContent = (item) => {
      if (hasChildren) {
        return (
          <Ripples
            onClick={() => openNewLevelMenu(item && item.id)}
            className="has-children"
          >
            <button
              type="button"
              className="mobile-menu-action-button"
              style={mobileMenuActionButtonStyle}
              onClick={() => openNewLevelMenu(item && item.id)}
            >
              {getTitleOfItem(item, isEnglish)}
              <div className="icon-right">
                <RightOutlined />
              </div>
            </button>
          </Ripples>
        );
      }
      //  TODO: try create component for this because this logic is almost the same as in MainNavigation
      if (item.item_type === 'external') {
        return (
          <Ripples>
            <div style={{ margin: '10px', width: '100%' }}>
              <ClearedExternalLink href={item.destination}>
                {getTitleOfItem(item, isEnglish)}
                <i
                  className="fas fa-external-link-alt"
                  style={{
                    transform: 'scale(0.75)',
                    display: 'inline-block',
                    verticalAlign: 'top',
                    marginLeft: '0.5em',
                  }}
                />
              </ClearedExternalLink>
            </div>
          </Ripples>
        );
      }
      const url = getMenuItemUrl(item, isEnglish);

      return (
        <Ripples>
          <div style={{ margin: '10px', width: '100%' }}>
            <ClearedLink to={url}>
              {getTitleOfItem(item, isEnglish)}
            </ClearedLink>
          </div>
        </Ripples>
      );
    };

    return (
      <li className={`child-level ${activeClass(item.menu_item_parent)}`}>
        {getItemContent(item)}
      </li>
    );
  };

  const backLink = (itemToOpen, activeClassId, title) => {
    return (
      <li className={`child-level header ${activeClass(activeClassId)}`}>
        <Ripples onClick={() => openNewLevelMenu(itemToOpen && itemToOpen.id)}>
          <button
            type="button"
            className="mobile-menu-action-button menu-back-top"
            style={mobileMenuActionButtonStyle}
            onClick={() => openNewLevelMenu(itemToOpen && itemToOpen.id)}
          >
            {t(title)}
            <div className="icon-right">
              <LeftOutlined /> {t('Späť')}
            </div>
          </button>
        </Ripples>
      </li>
    );
  };

  const activeClass = (id) => {
    return openedItemId && openedItemId === id ? 'show' : '';
  };

  const renderMenu = (
    <>
      <ul className={`mobile-navigation`}>
        {menu &&
          menu.map((item) => (
            <React.Fragment key={`first-level-${item.id}`}>
              {firstLevelMenuItem(item)}

              {item.children &&
                backLink(undefined, item.id, getTitleOfItem(item))}

              {item.children &&
                item.children.map((secondLevel) => (
                  <ul key={`second-level-${secondLevel.id}`}>
                    {childLevelMenuItem(secondLevel)}

                    {secondLevel.children &&
                      backLink(
                        item,
                        secondLevel.id,
                        getTitleOfItem(secondLevel, isEnglish),
                      )}

                    {secondLevel.children &&
                      secondLevel.children.map((thirdLevel) => (
                        <ul key={`third-level-${thirdLevel.id}`}>
                          {childLevelMenuItem(thirdLevel)}

                          {thirdLevel.children &&
                            backLink(
                              secondLevel,
                              thirdLevel.id,
                              getTitleOfItem(thirdLevel, isEnglish),
                            )}

                          {thirdLevel.children &&
                            thirdLevel.children.map((fourthLevel) => (
                              <ul key={`fourth-level-${fourthLevel.id}`}>
                                {childLevelMenuItem(fourthLevel)}
                              </ul>
                            ))}
                        </ul>
                      ))}
                  </ul>
                ))}
            </React.Fragment>
          ))}
      </ul>
    </>
  );

  return (
    <div
      id="main-mobile-navigation"
      className={`main-mobile-navigation-container ${withHideClass && 'hide'}`}
      role="navigation"
      aria-label={t('Hlavné menu')}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();

          if (openedItemId) {
            openItem(undefined);
            return;
          }

          closeMenu();
        }
      }}
    >
      <div className="mobile-navigation-wrapper">{renderMenu}</div>
    </div>
  );
};

MobileMenu.propTypes = {
  menu: PropTypes.array,
  withHideClass: PropTypes.bool,
  closeMenu: PropTypes.func,
};

export default MobileMenu;
